'use client';

import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { LoginForm } from '@/platform/auth/login-form';
import { AmbientBackground } from '@/platform/ui/ambient-background';
import { DEFAULT_BRANDING, type ProductBranding } from '@/platform/hooks/use-product-branding';

const highlights = [
  'Structured team and individual assessments in one place',
  'Clear, actionable reports your whole team can act on',
  'Track progress across every assessment from your dashboard',
];

export default function LoginPage() {
  const [branding, setBranding] = useState<ProductBranding>(DEFAULT_BRANDING);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/api/branding`, {
          headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string },
        });
        if (!res.ok) return;
        const data = await res.json();
        const settings = data?.settings ?? {};
        if (cancelled) return;
        setBranding({
          containerName: settings.containerName || DEFAULT_BRANDING.containerName,
          containerIconUrl: settings.containerIconUrl ?? null,
          products: {
            productA: {
              name: settings.products?.productA?.name || DEFAULT_BRANDING.products.productA.name,
              tagline: settings.products?.productA?.tagline || DEFAULT_BRANDING.products.productA.tagline,
              iconUrl: settings.products?.productA?.iconUrl ?? null,
              defaultIcon: DEFAULT_BRANDING.products.productA.defaultIcon,
            },
            productB: {
              name: settings.products?.productB?.name || DEFAULT_BRANDING.products.productB.name,
              tagline: settings.products?.productB?.tagline || DEFAULT_BRANDING.products.productB.tagline,
              iconUrl: settings.products?.productB?.iconUrl ?? null,
              defaultIcon: DEFAULT_BRANDING.products.productB.defaultIcon,
            },
          },
        });
      } catch {
        // Use defaults on error
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { containerName, containerIconUrl, products } = branding;

  function BrandMark({ compact = false }: { compact?: boolean }) {
    if (containerIconUrl) {
      return <img src={containerIconUrl} alt="" className="h-9 w-9 rounded-lg object-cover" />;
    }

    return (
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg font-bold ${compact ? 'bg-primary/10 text-primary' : 'bg-primary-foreground/15'}`}>
        {containerName.charAt(0)}
      </span>
    );
  }

  return (
    <>
      <AmbientBackground />
      <div className="flex min-h-screen">
        <div className="hidden lg:flex flex-col justify-between w-[45%] max-w-xl p-10 bg-primary text-primary-foreground">
          <div className="flex items-center gap-2">
            <BrandMark />
            <span className="font-semibold">{containerName}</span>
          </div>
          <div className="space-y-8">
            <h2 className="text-3xl font-bold leading-tight tracking-tight">
              Understand your brand. Align your team.
            </h2>
            <div className="space-y-4">
              <div>
                <p className="font-medium">{products.productA.name}</p>
                <p className="text-sm text-primary-foreground/70">{products.productA.tagline}</p>
              </div>
              <div>
                <p className="font-medium">{products.productB.name}</p>
                <p className="text-sm text-primary-foreground/70">{products.productB.tagline}</p>
              </div>
            </div>
          </div>
          <ul className="space-y-3">
            {highlights.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-primary-foreground/80">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md space-y-5">
            <div className="flex items-center gap-3 px-1 text-primary lg:hidden">
              <BrandMark compact />
              <div>
                <p className="font-semibold leading-tight">{containerName}</p>
                <p className="text-sm text-muted-foreground">Understand your brand. Align your team.</p>
              </div>
            </div>
            <LoginForm />
          </div>
        </div>
      </div>
    </>
  );
}
